import { useRef, ChangeEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import type { Dispatch } from 'redux';

import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { isFileExtensionValid } from 'CorePackage/MainModule/validators/isFileExtensionValid';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { addNoticeKnmFileSizeErrorBig } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorBig';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';

import { IChangePlanStatusToConsiderModalForm } from '../interfaces/IChangePlanStatusToConsiderModalForm';
import { REQUIRED_ERROR } from '../../MainModule/constants/validationErrors';

const MAX_FILE_SIZE = 10 * MB;
const documentValidExtensions = ['ods', 'xls', 'xlsx', 'doc', 'docx', 'pdf'];
const documentValidExtensionsString = `.${documentValidExtensions.join(',.')}`;

type ChangePlanStatusToConsiderModalFormRenderProps = FormRenderProps<IChangePlanStatusToConsiderModalForm>;

interface IChangePlanStatusToConsiderModalProps {
  visible: boolean;
  onApply: (values: IChangePlanStatusToConsiderModalForm) => void;
  onCloseModal: () => void;
}

export const ChangePlanStatusToConsiderModal = ({
  visible,
  onApply,
  onCloseModal,
}: IChangePlanStatusToConsiderModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const formRef = useRef<ChangePlanStatusToConsiderModalFormRenderProps['form']>();
  const { uploadPlanDocumentAttachment } = useKnmDocumentAttachment();

  const handleFormSubmit = useCallback(
    (values: IChangePlanStatusToConsiderModalForm) => {
      if (values.document) {
        onApply(values);
      }
    },
    [onApply],
  );

  const handleFileInputChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (file) {
        const isSizeValid = file.size <= MAX_FILE_SIZE;

        if (!isSizeValid) {
          dispatch(addNoticeKnmFileSizeErrorBig());
        } else {
          formRef?.current?.change('document', {
            fileName: file.name,
            fileSize: Math.trunc(file.size / 1000) || 0,
            isNew: true,
            file,
            signatureInfo: null,
          });
          const attachment = await uploadPlanDocumentAttachment(file);

          if (attachment) {
            formRef?.current?.change('document', {
              ...attachment,
              isNew: true,
            });
          }
        }
      }
      e.target.value = '';
    },
    [dispatch, uploadPlanDocumentAttachment],
  );

  const handleAfterClose = useCallback(() => {
    formRef.current?.restart();
  }, []);

  const removeFile = useCallback(() => formRef.current?.change('document'), []);

  function renderForm({ handleSubmit, form, values, invalid }: ChangePlanStatusToConsiderModalFormRenderProps) {
    formRef.current = form;
    const { document } = values;
    const documentName = document?.fileName;
    const isDocumentValid = !!documentName && isFileExtensionValid(documentName, documentValidExtensions);
    const isDocumentUploaded = document?.dateCreate;

    return (
      <form onSubmit={handleSubmit}>
        <ModalHeader sizeText="small" title="Смена статуса" color="black" />
        <ModalBody size="content" border="bottom">
          <Row>
            <FieldBlock label="ФИО уполномоченного прокурора, осуществившего рассмотрение данного плана" color="black">
              <TextInputField id="fullName" name="fullName" validate={makeRequired(REQUIRED_ERROR)} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock
              label="Должность прокурора, уполномоченного на рассмотрение и осуществившего рассмотрение данного плана"
              color="black"
            >
              <TextInputField id="position" name="position" validate={makeRequired(REQUIRED_ERROR)} />
            </FieldBlock>
          </Row>
          <Row>
            Приложите скан-образ документа в адрес органа контроля (надзора) о результатах рассмотрения плана,
            подписанное уполномоченным прокурором, осуществившим рассмотрение данного плана
          </Row>
          <Row notSpace align="center">
            <label className="shared-file-button" htmlFor="documentFile">
              Выберите файл
            </label>
            <input
              id="documentFile"
              name="documentFile"
              type="file"
              accept={documentValidExtensionsString}
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            {(!document || !isDocumentValid) && <span className="shared-field-label">Нет выбранных файлов</span>}
          </Row>
          {document && !isDocumentValid && <span className="shared-input-error">Неверный формат файла</span>}
          {document && isDocumentValid && (
            <UploadFile
              fileName={document.fileName || ''}
              fileSize={document.fileSize}
              progress={isDocumentUploaded ? 100 : 0}
              onRemove={removeFile}
            />
          )}
        </ModalBody>
        <ModalActions>
          <Button
            text="Далее"
            color="primary"
            size="medium"
            type="submit"
            visuallyDisabled={invalid || !isDocumentValid || !isDocumentUploaded}
          />
        </ModalActions>
      </form>
    );
  }

  return (
    <Modal visible={visible} onClose={onCloseModal} afterClose={handleAfterClose}>
      <Form<IChangePlanStatusToConsiderModalForm> render={renderForm} onSubmit={handleFormSubmit} />
    </Modal>
  );
};
